/**
 * The dates a workspace puts on its own calendar.
 *
 * ## WHY THIS IS NOT IN `calendar.ts`
 *
 * `calendar.ts` is about a curated public dataset: which slugs are worth
 * showing, what the product calls them, and how a run of days collapses. None of
 * that applies here. A moment has no slug, is never curated, is never collapsed
 * and is never renamed by the product — it is what somebody typed. The two files
 * stay apart so a rule written for one cannot be read as governing the other.
 * `calendar.ts` imports `ownMomentSchema` for the response's second array and
 * nothing else crosses.
 *
 * ## "OWN MOMENT", NOT "TENANT MOMENT"
 *
 * The table is `tenant_moments`, following `tenant_features`. Everything a
 * person reads says "your moments", because "tenant" is not a word this product
 * shows anybody. The wire uses `ownMoments` so the two are never confused with
 * `CALENDAR_MOMENTS` or `upcomingMoments()`, both of which mean a curated
 * national holiday out of a JSON file.
 *
 * ## THE DATE IS A DAY, AND THE SCHEMA FOR ONE ALREADY EXISTS
 *
 * `calendarDateSchema` from `./analytics.js` is reused rather than rewritten. It
 * is a regex plus a `parseCalendarDate` round-trip, so `2027-02-30` matches the
 * regex and is still refused instead of rolling silently into March, and its own
 * comment already makes this feature's argument: "A date picker does not produce
 * an instant — `2026-09-30` is not a moment until somebody says *where*."
 *
 * A second copy would be the fourth date conversion in this repository
 * (`localToday`, `toDateInputValue`, `apps/admin/src/lib/date-input.ts`), and the
 * admin file's header already names what the fourth one costs: "the obvious
 * second implementation is `new Date(value).toISOString()`, which is midnight
 * UTC, and a subscription written that way expires up to a day earlier than the
 * one written by the create form."
 *
 * ## WHERE "IN THE FUTURE" IS ENFORCED, AND WHY IT IS ENFORCED TWICE
 *
 * The browser makes the real cut, against `localToday()` — the reader's own wall
 * clock, the only clock that knows what "today" means to them. That is where the
 * sentence a person reads comes from.
 *
 * The API cannot repeat that test: it does not know the reader's zone, `tenants`
 * has no timezone column, and inventing one from the country is the assumption
 * this product is forbidden to make. So it refuses a narrower thing — what is
 * past for **every reader on earth** — using {@link CALENDAR_WINDOW_SLACK_DAYS},
 * the same one day the read path already allows, for the same reason: UTC−12 to
 * UTC+14 puts any reader's date within one day of UTC's.
 *
 * Without that slack this refuses a date the reader can see is today. At 18:00
 * in Los Angeles it is already tomorrow in UTC, so a strict server-side
 * `date >= utcToday` rejects a sale starting this evening with a message telling
 * the reader to pick a date in the future. Slice 33 removed `daysAway` from the
 * wire to stop the read path making that mistake; this is the same mistake
 * pointing the other way, and a write has no second cut downstream to absorb it.
 *
 * ## AND WHY THE SIXTY-DAY WINDOW IS NOT A CEILING
 *
 * A date further out than {@link CALENDAR_WINDOW_DAYS} is accepted. Refusing it
 * would mean telling somebody their March conference is not a date, and bounding
 * the picker at sixty days would be a control that silently will not go where
 * the person is pointing. What is not acceptable is accepting it and then
 * showing nothing — that is the same broken promise as a button labelled "add a
 * moment" that opens a campaign form. So the read is unwindowed at both ends,
 * every row is listed under "Your moments" on the card, and the toast says which
 * of the two happened at the moment the person acts.
 */
import { z } from 'zod';

import { uuidSchema } from '../common.js';
import { calendarDateSchema } from './analytics.js';

/**
 * Characters a workspace's own moment name may run to.
 *
 * 120, deliberately the same number as `MAX_SEGMENT_NAME_LENGTH`: a segment and
 * a moment are the two things a person names in this product, and two numbers
 * would be two answers to one question. `tenant_moments_name_length` in 0026 is
 * the same number, so the data obeys it and not only this code path.
 */
export const MAX_OWN_MOMENT_NAME_LENGTH = 120;

/**
 * Moments one workspace may hold at once, counted over **every** row it has.
 *
 * A guard against a runaway client and against a table nobody prunes, not a
 * product limit — the read is a single index scan over at most this many rows.
 * Counted over everything rather than over future rows only, because a cap that
 * ignored history would let the table grow without bound while the screen kept
 * saying there was room.
 */
export const MAX_OWN_MOMENTS = 100;

export const ownMomentNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_OWN_MOMENT_NAME_LENGTH);

/**
 * One moment the workspace wrote itself.
 *
 * It carries an **id**, which is the whole reason it is not a `CalendarEntry`: a
 * curated holiday is a fact about a country and there is nothing to do to one,
 * while this is a row somebody wrote and must be able to remove. And it carries
 * **one name, not a list** — the grouping that folds four festivals onto 14
 * April is about a planning day, and a workspace's own moment is a row it can
 * point at.
 *
 * No `daysAway`, for exactly the reason slice 33 removed it from
 * `calendarEntrySchema`: a countdown computed against the server's UTC date is
 * one too many for a reader at UTC+05:30 for five and a half hours out of every
 * twenty-four. The browser computes it.
 */
export const ownMomentSchema = z
  .object({
    id: uuidSchema,
    /** `YYYY-MM-DD`. No time, because a date is not an instant. */
    date: calendarDateSchema,
    /** Exactly what the person typed. Never a curated name, never merged with one. */
    name: ownMomentNameSchema,
  })
  .strict();
export type OwnMoment = z.infer<typeof ownMomentSchema>;

export const addOwnMomentRequestSchema = z
  .object({ date: calendarDateSchema, name: ownMomentNameSchema })
  .strict();
export type AddOwnMomentRequest = z.infer<typeof addOwnMomentRequestSchema>;

export const ownMomentResponseSchema = z.object({ moment: ownMomentSchema }).strict();
export type OwnMomentResponse = z.infer<typeof ownMomentResponseSchema>;

export const ownMomentParamsSchema = z.object({ momentId: uuidSchema }).strict();
export type OwnMomentParams = z.infer<typeof ownMomentParamsSchema>;

/**
 * The refusals, said in one place because the form prints them and the service
 * throws them.
 *
 * Copy is part of the code in this repository, and a form that says one thing
 * while the API says another about the same refusal is the shape half this
 * repo's review findings take.
 */
export const OWN_MOMENT_COPY = {
  missingDate: 'Pick a date.',
  unrealDate: 'That is not a day on the calendar.',
  pastDate: 'That day has gone. Pick today or a day after it.',
  missingName: 'Give it a name, so the calendar has something to show.',
  longName: `Keep the name under ${String(MAX_OWN_MOMENT_NAME_LENGTH)} characters.`,
  limit: `You already have ${String(MAX_OWN_MOMENTS)} moments. Remove one to add another.`,
  missing: 'That moment is not on your calendar.',
} as const;

/*
 * `ownMomentDateIsAddable` LIVES IN `calendar.ts`, NOT HERE.
 *
 * It needs `daysBetween`, which `calendar.ts` owns and three suites import
 * from there. Importing it into this file would make the two modules circular
 * — `calendar.ts` already imports `ownMomentSchema` from here for the
 * response's second array — and a cycle whose resolution depends on which
 * module the barrel lists first is a cycle that breaks the day somebody
 * reorders an export. Moving `daysBetween` instead was refused for the reason
 * nothing else moved in this slice: the suites import it where it is.
 *
 * So the one function that needs a date helper sits beside the date helpers,
 * and this file stays what its header says it is — the shape of a moment, and
 * the rules about its name.
 */

